import useControlledState from '@quid/react-use-controlled-state';
import RcTooltip from 'rc-tooltip';
import { TooltipProps as RcTooltipProps } from 'rc-tooltip/es/Tooltip';
import React, { cloneElement, forwardRef, isValidElement, ReactNode, useRef, useMemo, useEffect } from 'react';
import { useMergeRefs } from 'use-callback-ref';
import { OverflowContext } from './OverflowContext';
import { OverflowTrigger } from './OverflowTrigger';
import { TooltipPaper } from './Paper';
import { Placement } from './placements';
import { TooltipThemeProps } from './theme';
import { Global } from './Tooltip.styles';
import { uuid } from './uuid';

type Trigger = 'hover' | 'click' | 'focus';

export interface TooltipProps extends Omit<RcTooltipProps, 'overlay' | 'placement' | 'trigger'>, TooltipThemeProps {
  title: ReactNode;
  placement?: Placement;
  trigger?: Trigger | Trigger[];
}

const TooltipCompoundParent = forwardRef<HTMLElement, TooltipProps>(({ title, visible, onVisibleChange, maxWidth, children, ...rest }, ref) => {
  const [visibleControlled, onVisibleChangeControlled] = useControlledState(visible === undefined ? false : undefined, visible, onVisibleChange);
  const { current: overflowTriggers } = useRef(new Set<() => boolean>());
  const childrenRef = useRef<HTMLInputElement>();
  const tooltipId = useMemo(uuid, []);

  const handleVisibleChange: TooltipProps['onVisibleChange'] = (value) => {
    if (value) {
      const noOverflowTriggers = overflowTriggers.size === 0;
      const hasOverflowTriggerCut = Array.from(overflowTriggers).some((isOverflowActive) => isOverflowActive());
      if (noOverflowTriggers || hasOverflowTriggerCut) {
        onVisibleChangeControlled(value);
      }
    } else {
      onVisibleChangeControlled(false);
    }
  };

  if (!isValidElement(children)) {
    throw new Error('Tooltip accepts single element as a child');
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore TODO try with useImperativeHandle, also find way to get ref in TS
  const mergedRef = useMergeRefs([ref, children?.ref, childrenRef]);

  useEffect(() => {
    childrenRef.current?.setAttribute('data-trigger-id', tooltipId);
  }, [childrenRef, tooltipId]);

  return (
    <>
      <Global id="global-tooltip-styles" />
      <OverflowContext.Provider value={overflowTriggers}>
        <RcTooltip
          visible={!!title && visibleControlled}
          onVisibleChange={handleVisibleChange}
          mouseEnterDelay={0.3}
          {...rest}
          id={tooltipId}
          overlay={<TooltipPaper maxWidth={maxWidth}>{title}</TooltipPaper>}
        >
          {cloneElement(children, { ref: mergedRef })}
        </RcTooltip>
      </OverflowContext.Provider>
    </>
  );
});

TooltipCompoundParent.defaultProps = {
  animation: 'zoom',
  placement: 'bottom',
};

export const Tooltip = Object.assign(TooltipCompoundParent, { OverflowTrigger });
